import React, { useRef, useEffect, useState } from "react";
import GameCard from "../gamecard/gameCard.jsx";
import { useNavigate } from 'react-router-dom';
import slugify from "../../utils/slugify";

const ScrollableCategory = ({ category, games }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const pauseDuration = 3000;
  const scrollDuration = 600;

  const handleCategoryClick = (category) => {
    category.title = slugify(category.title)
    navigate(`/games/${category.title}?c=${category.id}`);
  };

  useEffect(() => {
    if (scrollRef.current) {
      const firstCard = scrollRef.current.querySelector(".gameCardClass");
      if (firstCard) {
        setCardWidth(Math.floor(firstCard.getBoundingClientRect().width + 16));
      }
    }
  }, []);

  useEffect(() => {
    if (cardWidth === 0 || !scrollRef.current) return;

    let animationFrameId;
    let timeoutId;

    const easeInOutQuad = (t) => t < 0.5
      ? 2 * t * t
      : -1 + (4 - 2 * t) * t;

    const smoothScroll = () => {
      if (!scrollRef.current || isDragging) return;

      const slider = scrollRef.current;
      const startScroll = slider.scrollLeft;
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      let targetScroll = startScroll + cardWidth;

      if (targetScroll >= maxScroll) {
        targetScroll = 0; // wrap around
      }

      const duration = scrollDuration;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutQuad(progress);

        slider.scrollLeft = startScroll + (targetScroll - startScroll) * easedProgress;

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animate);
        } else {
          timeoutId = setTimeout(smoothScroll, pauseDuration);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
    };

    timeoutId = setTimeout(smoothScroll, pauseDuration);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
    };
  }, [cardWidth, isDragging]);


  // Handle touch and drag interactions
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.type === "touchstart" ? e.touches[0].pageX : e.pageX);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const slider = scrollRef.current;
    const x = e.type === "touchmove" ? e.touches[0].pageX : e.pageX;
    const walk = (x - startX) * 1.5;
    slider.scrollLeft = scrollLeft - walk;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="mb-6 px-2 md:px-4">
      {/* Header Section */}
      <div className="bg-white/10 backdrop-blur-md p-3 md:p-6 rounded-lg border border-white/20 shadow-lg flex justify-between items-center mb-3 md:mb-4 bg-[#121212] px-3 md:px-6 py-2 md:py-3 shadow-md border border-[#00F0FF]">
        <h2 className="text-[#00F0FF] text-sm md:text-xl font-bold tracking-wide uppercase">
          {category.title}
        </h2>
        <button
          onClick={() => handleCategoryClick(category)}
          className="px-3 md:px-5 py-1 md:py-1.5 text-[#121212] bg-[#00F0FF] font-semibold rounded-md hover:bg-[#FF007F] transition-all shadow-md hover:shadow-lg tracking-wide transform hover:scale-105 text-xs md:text-base"
        >
          Explore &raquo;
        </button>
      </div>


      {/* Scrollable Games Section */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto space-x-3 md:space-x-4 px-2 py-3 cursor-grab active:cursor-grabbing scrollbar-hidden bg-[#1A1A1A] rounded-lg shadow-md border border-[#00F0FF] hover:border-[#FF007F] transition-all transform hover:scale-[1.02]"
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          scrollSnapType: "x mandatory",
          whiteSpace: "nowrap",
          overflowX: "scroll",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {games.map((game, index) => (
          <GameCard key={game.id} game={game} index={index} />
        ))}
      </div>
    </div>
  );
};

export default ScrollableCategory;
